import * as XLSX from 'xlsx';
import { normalizeHeader } from './productImport';
import { RECORD_TYPES } from './recordTypes';

/* ============================================================
   Reading cells
============================================================ */

export const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

// Excel keeps some numbers with float noise; show them the way Excel does.
export function cellText(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Number(value.toPrecision(12)));
  }
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  return String(value ?? '');
}

export const norm = (value) => cellText(value).replace(/\s+/g, ' ').trim().toLowerCase();

export const line = (value) => (isBlank(value) ? '' : cellText(value).replace(/\s+/g, ' ').trim());

// Ivy separates lines with a lone \r.
export const multiline = (value) =>
  isBlank(value)
    ? ''
    : cellText(value)
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map((part) => part.trimEnd())
        .join('\n')
        .trim();

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// What people type when a form insists on an email: no@email.com, na@email.com.
const PLACEHOLDER_LOCAL_PARTS = new Set(['no', 'na', 'n/a', 'none', 'noemail', 'nomail']);
const PLACEHOLDER_DOMAINS = new Set(['example.com', 'noemail.com', 'none.com']);

export function isPlaceholderEmail(email) {
  const [local, domain] = email.toLowerCase().split('@');
  return PLACEHOLDER_LOCAL_PARTS.has(local) || PLACEHOLDER_DOMAINS.has(domain);
}

// The first address in the cell ("a@x.com; b@y.com" keeps a@x.com). Stand-ins
// are dropped rather than saved, and counted so the review can say so.
export function readEmail(value) {
  const found =
    line(value)
      .split(/[\s,;]+/)
      .find((part) => EMAIL.test(part)) || '';
  if (found && isPlaceholderEmail(found)) return { email: '', placeholder: true };
  return { email: found, placeholder: false };
}

const US_STATES = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'puerto rico': 'PR',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
};
const STATE_CODES = new Set(Object.values(US_STATES));

// "California", "ca" and "CA " all become CA, the way the New Project form
// lists states. Anything else ("Portugal") is kept as written.
export function readState(value) {
  const text = line(value);
  return (
    US_STATES[text.toLowerCase()] ||
    (STATE_CODES.has(text.toUpperCase()) ? text.toUpperCase() : text)
  );
}

// Excel drops the leading zero of New England zips (02210 is stored as 2210).
export function readZip(value) {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 100 && value < 100000) {
    return String(value).padStart(5, '0');
  }
  return line(value);
}

const TRUE_WORDS = new Set([
  'true',
  'yes',
  'y',
  '1',
  'x',
  'archived',
  'inactive',
  'closed',
  'completed',
  'complete',
]);
const FALSE_WORDS = new Set(['false', 'no', 'n', '0', 'active', 'open']);

// true, false, or null when the cell doesn't say.
export function readBool(value) {
  if (value === true || value === false) return value;
  if (typeof value === 'number') return value !== 0;
  const word = norm(value);
  if (TRUE_WORDS.has(word)) return true;
  if (FALSE_WORDS.has(word)) return false;
  return null;
}

export function readNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = line(value).replace(/[$,%\s]/g, '');
  if (!text) return null;
  const negative = /^\(.*\)$/.test(text);
  const n = Number(text.replace(/[()]/g, ''));
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

// Hours as a number (1.5), clock time (1:30) or words (1h 30m, 90 min).
export function readHours(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = norm(value);
  if (!text) return null;
  const clock = text.match(/^(\d+):(\d{1,2})$/);
  if (clock) return Number(clock[1]) + Number(clock[2]) / 60;
  const words = text.match(
    /^(?:(\d+(?:\.\d+)?)\s*h(?:ours?|rs?)?)?\s*(?:(\d+(?:\.\d+)?)\s*m(?:in(?:ute)?s?)?)?$/
  );
  if (words && (words[1] || words[2])) return Number(words[1] || 0) + Number(words[2] || 0) / 60;
  return readNumber(value);
}

const pad = (n) => String(n).padStart(2, '0');

function partsFromText(text) {
  let m = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m)
    return { y: +m[1], mo: +m[2], d: +m[3], h: +(m[4] || 0), mi: +(m[5] || 0), s: +(m[6] || 0) };
  m = text.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?)?$/i
  );
  if (m) {
    let y = +m[3];
    if (y < 100) y += y < 70 ? 2000 : 1900;
    let h = +(m[4] || 0);
    if (m[7]) h = (h % 12) + (m[7].toLowerCase() === 'pm' ? 12 : 0);
    return { y, mo: +m[1], d: +m[2], h, mi: +(m[5] || 0), s: +(m[6] || 0) };
  }
  const ms = Date.parse(text);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  return {
    y: d.getFullYear(),
    mo: d.getMonth() + 1,
    d: d.getDate(),
    h: d.getHours(),
    mi: d.getMinutes(),
    s: d.getSeconds(),
  };
}

// Excel files are read raw, so dates arrive as day counts; they're turned back
// into the date and time written in the cell. Text dates ("2026-03-02",
// "3/2/26", "March 2, 2026") work too. Returns { date, iso } or null.
export function readDate(value, { date1904 = false } = {}) {
  let p = null;
  if (typeof value === 'number') {
    const code =
      Number.isFinite(value) && value >= 1 ? XLSX.SSF.parse_date_code(value, { date1904 }) : null;
    if (code)
      p = { y: code.y, mo: code.m, d: code.d, h: code.H, mi: code.M, s: Math.floor(code.S) };
  } else if (!isBlank(value)) {
    p = partsFromText(line(value));
  }
  if (!p || p.y < 1900 || p.y > 2100) return null;
  const date = `${p.y}-${pad(p.mo)}-${pad(p.d)}`;
  const check = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(check.getTime()) || check.toISOString().slice(0, 10) !== date) return null;
  return { date, iso: `${date}T${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}.000Z` };
}

export function localToday(now = new Date()) {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// FNV-1a: plenty to tell one row's content from another's.
export function hashText(text) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

// Where a row sits in the file plus a fingerprint of what it says. The server
// derives the saved record's id from it, so importing the same row again
// (a retry, a second run) finds the first copy instead of making another,
// while a different row on a fresh export doesn't collide with it.
export function importRefFor(sheetName, row) {
  return `${sheetName}#${row.rowNumber}:${hashText(row.cells.map(cellText).join('\u0001'))}`;
}

/* ============================================================
   Column mapping
============================================================ */

export function recordFields(type) {
  return RECORD_TYPES[type].fields;
}

export function recordHeaderSet(type) {
  return new Set(recordFields(type).flatMap((field) => field.headers));
}

export function autoMapRecordColumns(type, headers) {
  const normalized = headers.map(normalizeHeader);
  const mapping = headers.map(() => '');
  recordFields(type).forEach((field) => {
    for (const candidate of field.headers) {
      const col = normalized.findIndex((header, i) => header === candidate && !mapping[i]);
      if (col !== -1) {
        mapping[col] = field.key;
        return;
      }
    }
  });
  return mapping;
}

// What has to be mapped before the review can be built, as a message, or ''.
export function missingMapping(type, mapping) {
  const has = (key) => mapping.includes(key);
  switch (type) {
    case 'customers':
      return has('name') || has('company') || has('firstName') || has('lastName')
        ? ''
        : 'Choose which column holds the client name.';
    case 'vendors':
      return has('name') || has('firstName') || has('lastName')
        ? ''
        : 'Choose which column holds the vendor name.';
    case 'projects':
      return has('name') ? '' : 'Choose which column holds the project name.';
    case 'taxLocations':
      if (!has('name') && !has('city') && !has('state')) {
        return 'Choose the column with the tax location (city and state).';
      }
      return has('rate') ? '' : 'Choose which column holds the tax rate.';
    case 'rooms':
      if (!has('projectName')) return 'Choose which column holds the project name.';
      return has('name') ? '' : 'Choose which column holds the room name.';
    case 'timeEntries':
      if (!has('projectName')) return 'Choose which column holds the project name.';
      if (!has('date')) return 'Choose which column holds the date.';
      return has('hours') ? '' : 'Choose which column holds the hours.';
    case 'proposals':
      if (!has('projectName')) return 'Choose which column holds the project name.';
      if (!has('name')) return 'Choose which column holds the item name.';
      return has('unitPrice') || has('totalPrice') || has('unitCost')
        ? ''
        : 'Choose which column holds the price.';
    default:
      return '';
  }
}

// The first value in a column, as the review will read it.
export function recordColumnSample(type, dataRows, col, fieldKey, { date1904 } = {}) {
  const found = dataRows.find((row) => !isBlank(row.cells[col]));
  if (!found) return '';
  const raw = found.cells[col];
  const field = recordFields(type).find((f) => f.key === fieldKey);
  if (field?.kind === 'date') return readDate(raw, { date1904 })?.date || line(raw);
  if (field?.kind === 'bool') {
    const value = readBool(raw);
    return value === null ? line(raw) : value ? 'Yes' : 'No';
  }
  // Any other date cell shows as Excel shows it, not as its day count.
  return found.dates?.[col] || line(raw);
}

// A field can come from one column only; choosing it takes it off any other.
export function assignRecordField(mapping, col, field) {
  return mapping.map((current, i) => {
    if (i === col) return field;
    return field && current === field ? '' : current;
  });
}

/* ============================================================
   Review rows
============================================================ */

const str = (value) => (typeof value === 'string' ? value : '');

// One file row as the type's fields, plus how many stand-in emails were dropped.
export function readRow(type, row, mapping, { date1904 }) {
  const fields = new Map(recordFields(type).map((field) => [field.key, field]));
  const values = {};
  let placeholderEmails = 0;
  mapping.forEach((key, col) => {
    const field = fields.get(key);
    if (!field) return;
    const raw = row.cells[col];
    switch (field.kind) {
      case 'email': {
        const { email, placeholder } = readEmail(raw);
        values[key] = email;
        if (placeholder) placeholderEmails += 1;
        break;
      }
      case 'multiline':
        values[key] = multiline(raw);
        break;
      case 'state':
        values[key] = readState(raw);
        break;
      case 'zip':
        values[key] = readZip(raw);
        break;
      case 'bool':
        values[key] = readBool(raw);
        break;
      case 'number':
        values[key] = readNumber(raw);
        break;
      case 'hours':
        values[key] = readHours(raw);
        break;
      case 'date':
        values[key] = readDate(raw, { date1904 });
        break;
      default:
        values[key] = line(raw);
    }
  });
  return { values, placeholderEmails };
}

const addressLine = (parts) =>
  [
    parts.street,
    parts.street2,
    [parts.city, [parts.state, parts.zip].filter(Boolean).join(' ')].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ');

// Rows with the same key become one. Blanks are filled from the later rows; the
// result stays archived only if every row was. `merge` handles anything else.
function combineRows(entries, keyOf, merge) {
  const groups = new Map();
  const ordered = [];
  entries.forEach((entry) => {
    const key = keyOf(entry);
    const existing = key ? groups.get(key) : null;
    if (!existing) {
      const copy = {
        ...entry,
        fields: { ...entry.fields },
        extra: { ...entry.extra },
        sourceRows: [...entry.sourceRows],
      };
      if (key) groups.set(key, copy);
      ordered.push(copy);
      return;
    }
    merge?.(existing, entry);
    ['fields', 'extra'].forEach((part) => {
      Object.keys(entry[part]).forEach((field) => {
        if (isBlank(existing[part][field]) && !isBlank(entry[part][field])) {
          existing[part][field] = entry[part][field];
        }
      });
    });
    existing.archived = existing.archived && entry.archived;
    existing.sourceRows.push(...entry.sourceRows);
  });
  return ordered;
}

function nameIndex(list, nameOf) {
  const index = new Map();
  list.forEach((item) => {
    const key = norm(nameOf(item));
    if (key) index.set(key, [...(index.get(key) || []), item]);
  });
  return index;
}

const projectName = (p) => p.name || p.customerName || '';

// A project in the studio picked by name, and by client name when one is given
// and settles it. Several still left: the first active one, flagged so the
// review shows it.
export function projectMatcher(projects) {
  const byName = nameIndex(projects, projectName);
  return (name, clientName = '') => {
    let found = byName.get(norm(name)) || [];
    if (!found.length) return null;
    const forClient = found.filter((p) => norm(p.customerName) === norm(clientName));
    if (clientName && forClient.length) found = forClient;
    const pick = found.find((p) => p.isActive !== false) || found[0];
    return { project: pick, ambiguous: found.length > 1 };
  };
}

// The studio client a project belongs to: the one matching both its client name
// and email, else the name, else the email. Ivy can hold several clients with
// one name, so the email usually settles it.
export function clientMatcher(customers) {
  // Indexed by name and email up front so matching a sheet of projects is
  // O(rows), not O(rows x clients) — the latter froze the tab on big studios.
  const byName = new Map();
  const byEmail = new Map();
  const add = (map, key, c) => {
    if (!key) return;
    const arr = map.get(key);
    if (arr) arr.push(c);
    else map.set(key, [c]);
  };
  customers.forEach((c) => {
    add(byName, norm(c.name), c);
    [c.email, c.secondaryEmail].forEach((e) => add(byEmail, norm(e), c));
  });
  return ({ name, email }) => {
    const wantName = norm(name);
    const wantEmail = norm(email);
    const scores = new Map();
    const bump = (c, s) => scores.set(c, (scores.get(c) || 0) + s);
    if (wantName) (byName.get(wantName) || []).forEach((c) => bump(c, 2));
    // A client listed under this email once, even if it's both their emails.
    if (wantEmail) new Set(byEmail.get(wantEmail) || []).forEach((c) => bump(c, 1));
    let best = [];
    let bestScore = 0;
    scores.forEach((score, c) => {
      if (score < bestScore) return;
      if (score > bestScore) {
        best = [];
        bestScore = score;
      }
      best.push(c);
    });
    return best.length ? { customer: best[0], ambiguous: best.length > 1 } : null;
  };
}

// "Venice, CA", "Atlanta, GA 30307", "California" or "Downey".
export function placeFromTaxName(name) {
  const text = line(name).replace(/\s+\d{5}(-\d{4})?$/, '');
  const comma = text.lastIndexOf(',');
  if (comma !== -1) {
    return { city: text.slice(0, comma).trim(), state: readState(text.slice(comma + 1)) };
  }
  const state = readState(text);
  return STATE_CODES.has(state) ? { city: '', state } : { city: text, state: '' };
}

export const taxRateOf = (location) => readNumber(location?.tax ?? location?.rate);

export function taxMatcher(taxLocations) {
  return (taxName) => {
    const place = placeFromTaxName(taxName);
    if (!place.city && !place.state) return null;
    return (
      taxLocations.find(
        (t) =>
          norm(t.city) === norm(place.city) &&
          (!place.state || !t.state || readState(t.state) === place.state)
      ) || null
    );
  };
}

const ALREADY = 'Already in this studio';
const IMPORTED_EARLIER = 'Imported into this studio before';

// Why a row is already covered by what the studio has, or ''. Matching on
// importRef finds rows an earlier run imported; the check just before saving
// leaves that to the server, which knows whether it was this run, so the row's
// own saved copy (a retry after a lost response) doesn't count against it.
// Precomputes the lookups existingReason compares against, so a whole sheet is
// checked in O(rows + studio records) rather than rescanning the studio's
// records for every row. Build it once, then pass it to each existingReason.
export function buildExistingIndex(type, context = {}) {
  const list = context[type] || [];
  const refs = new Set();
  list.forEach((item) => item.importRef && refs.add(item.importRef));
  const index = { refs };
  const add = (map, key, item) => {
    if (!key) return;
    const arr = map.get(key);
    if (arr) arr.push(item);
    else map.set(key, [item]);
  };
  switch (type) {
    case 'customers':
      index.byEmail = new Map();
      index.byName = new Map();
      list.forEach((c) => {
        [c.email, c.secondaryEmail].forEach((e) => add(index.byEmail, norm(e), c));
        add(index.byName, norm(c.name), c);
      });
      break;
    case 'vendors':
      index.byName = new Map();
      list.forEach((v) => add(index.byName, norm(v.name), v));
      break;
    case 'taxLocations':
      index.byPlace = new Map();
      list.forEach((t) => add(index.byPlace, `${norm(t.city)}|${readState(t.state)}`, t));
      break;
    case 'projects':
      index.byName = new Map();
      list.forEach((p) => add(index.byName, norm(projectName(p)), p));
      break;
    case 'rooms':
      index.byKey = new Map();
      list.forEach((r) => add(index.byKey, `${r.projectId}|${norm(r.name)}`, r));
      break;
    case 'timeEntries':
      index.byKey = new Map();
      list.forEach((e) =>
        add(
          index.byKey,
          `${e.projectId}|${e.date}|${Number(e.billableHours)}|${norm(e.description)}`,
          e
        )
      );
      break;
    case 'proposals':
      index.byNumber = new Map();
      list.forEach((p) => add(index.byNumber, norm(p.proposalNumber), p));
      break;
    default:
      break;
  }
  return index;
}

export function existingReason(type, row, context, { matchImportRef = true, index } = {}) {
  const idx = index || buildExistingIndex(type, context);
  if (matchImportRef && row.importRef && idx.refs.has(row.importRef)) return IMPORTED_EARLIER;
  const f = row.fields;
  // A bucket counts only if something in it isn't this row's own earlier import.
  const other = (bucket) => (bucket || []).some((item) => item.importRef !== row.importRef);
  switch (type) {
    case 'customers': {
      const email = norm(f.email);
      if (email && other(idx.byEmail.get(email))) {
        return 'A client with this email is already in this studio';
      }
      return other(idx.byName.get(norm(f.name)))
        ? 'A client with this name is already in this studio'
        : '';
    }
    case 'vendors':
      return other(idx.byName.get(norm(f.name))) ? ALREADY : '';
    case 'taxLocations': {
      const rate = readNumber(f.rate);
      const bucket = idx.byPlace.get(`${norm(f.city)}|${readState(f.state)}`) || [];
      return rate !== null &&
        bucket.some(
          (t) => t.importRef !== row.importRef && Math.abs((taxRateOf(t) ?? NaN) - rate) < 0.0001
        )
        ? ALREADY
        : '';
    }
    case 'projects': {
      const client = norm(row.extra.clientName);
      const bucket = idx.byName.get(norm(f.name)) || [];
      return bucket.some(
        (p) =>
          p.importRef !== row.importRef &&
          (!client || !p.customerName || norm(p.customerName) === client)
      )
        ? ALREADY
        : '';
    }
    case 'rooms':
      return other(idx.byKey.get(`${f.projectId}|${norm(f.name)}`)) ? ALREADY : '';
    case 'timeEntries': {
      const hours = readNumber(f.hours);
      return other(idx.byKey.get(`${f.projectId}|${f.date}|${hours}|${norm(f.description)}`))
        ? ALREADY
        : '';
    }
    case 'proposals': {
      const number = norm(f.proposalNumber);
      return number && other(idx.byNumber.get(number))
        ? 'A proposal with this number is already in this studio'
        : '';
    }
    default:
      return '';
  }
}

const roundTo = (value, places) => Number(value.toFixed(places));

function readEntries(type, sheet, mapping) {
  const meta = { date1904: Boolean(sheet.date1904) };
  return sheet.dataRows.map((row) => ({ row, ...readRow(type, row, mapping, meta) }));
}

const entryBase = (sheet, { row, values }) => ({
  importRef: importRefFor(sheet.name, row),
  sourceRows: [row.rowNumber],
  archived: values.archived === true,
});

// Labels a mixed contact list (Houzz Pro's) gives the people a studio buys from.
const VENDOR_ROLE =
  /\b(vendors?|subs?|sub-?contractors?|suppliers?|showrooms?|reps?|trades?|manufacturers?|installers?|workrooms?|fabricators?)\b/i;
export const isVendorRole = (role) => VENDOR_ROLE.test(role || '');

const splitName = (name) => {
  const [first = '', ...rest] = name.split(' ');
  return { first, last: rest.join(' ') };
};

function customerEntries(sheet, mapping) {
  return readEntries('customers', sheet, mapping).map((e) => {
    const v = e.values;
    const person = str(v.name) || [str(v.firstName), str(v.lastName)].filter(Boolean).join(' ');
    return {
      ...entryBase(sheet, e),
      placeholderEmails: e.placeholderEmails,
      vendorRow: isVendorRole(str(v.role)),
      fields: {
        name: str(v.accountName) || str(v.name) || str(v.company) || person,
        email: str(v.email),
        phone: str(v.phone),
        street: str(v.street),
        city: str(v.city),
        state: str(v.state),
        zip: str(v.zip),
      },
      extra: {
        company: str(v.company),
        secondaryEmail: str(v.secondaryEmail),
        secondaryPhone: str(v.secondaryPhone),
        street2: str(v.street2),
        notes: str(v.notes),
        accountName: str(v.accountName),
        tags: str(v.tags),
        people: person ? [{ name: person, role: str(v.role) }] : [],
        secondaryName: '',
      },
    };
  });
}

// A household's next person becomes the client's secondary contact; anyone
// past that is listed in its notes.
function mergeHousehold(existing, entry) {
  const x = existing.extra;
  if (!x.accountName) return;
  const [next] = entry.extra.people;
  if (!next) return;
  x.people = [...x.people, next];
  if (x.secondaryName || x.people.length > 2) return;
  x.secondaryName = next.name;
  // Without the first person's own, theirs becomes the client's email instead.
  if (entry.fields.email && existing.fields.email) x.secondaryEmail = entry.fields.email;
  if (entry.fields.phone && existing.fields.phone) x.secondaryPhone = entry.fields.phone;
}

function vendorEntries(sheet, mapping) {
  return readEntries('vendors', sheet, mapping).map((e) => {
    const v = e.values;
    const contact = splitName(str(v.contactName));
    const firstName = str(v.firstName) || contact.first;
    const lastName = str(v.lastName) || contact.last;
    return {
      ...entryBase(sheet, e),
      placeholderEmails: e.placeholderEmails,
      vendorRow: isVendorRole(str(v.role)),
      fields: {
        name: str(v.name) || [firstName, lastName].filter(Boolean).join(' '),
        email: str(v.email),
        phone: str(v.phone),
        link: str(v.link),
        city: str(v.city),
        state: str(v.state),
      },
      extra: {
        firstName,
        lastName,
        accountNumber: str(v.accountNumber),
        street: [str(v.street), str(v.street2)].filter(Boolean).join(', '),
        zip: str(v.zip),
        description: str(v.description),
        notes: str(v.notes),
      },
    };
  });
}

function taxEntries(sheet, mapping) {
  const entries = readEntries('taxLocations', sheet, mapping);
  // Rates kept as fractions (0.0975) are scaled up to percentages.
  const rates = entries.map((e) => e.values.rate).filter((r) => typeof r === 'number' && r);
  const scale = rates.length && rates.every((r) => Math.abs(r) <= 1) ? 100 : 1;
  return entries.map((e) => {
    const v = e.values;
    const place = placeFromTaxName(v.name);
    const rate = typeof v.rate === 'number' ? roundTo(v.rate * scale, 4) : null;
    return {
      ...entryBase(sheet, e),
      fields: {
        city: str(v.city) || place.city,
        state: str(v.state) || place.state,
        rate: rate === null ? '' : String(rate),
        agency: str(v.agency),
      },
      extra: { name: str(v.name) },
    };
  });
}

function projectEntries(sheet, mapping) {
  return readEntries('projects', sheet, mapping).map((e) => {
    const v = e.values;
    return {
      ...entryBase(sheet, e),
      fields: {
        name: str(v.name),
        clientChoice: '',
        taxChoice: '',
        street: str(v.street),
        city: str(v.city),
        status: v.archived === true ? 'archived' : 'active',
      },
      extra: {
        clientName: str(v.clientName),
        email: str(v.email),
        secondaryEmail: str(v.secondaryEmail),
        phone: str(v.phone),
        street2: str(v.street2),
        state: str(v.state),
        zip: str(v.zip),
        taxName: str(v.taxName),
        notes: [str(v.notes), str(v.notes2)].filter(Boolean).join('\n\n'),
        createdAt: v.createdAt?.iso || '',
        alsoAt: [],
      },
    };
  });
}

function roomEntries(sheet, mapping) {
  return readEntries('rooms', sheet, mapping).map((e) => ({
    ...entryBase(sheet, e),
    fields: { projectId: '', name: str(e.values.name) },
    extra: { projectName: str(e.values.projectName) },
  }));
}

function timeEntries(sheet, mapping) {
  return readEntries('timeEntries', sheet, mapping).map((e) => {
    const v = e.values;
    return {
      ...entryBase(sheet, e),
      fields: {
        date: v.date?.date || '',
        projectId: '',
        hours: typeof v.hours === 'number' ? String(roundTo(v.hours, 2)) : '',
        rate: typeof v.rate === 'number' ? String(roundTo(v.rate, 2)) : '',
        billable: v.billable !== false,
        description: str(v.description),
        employee: str(v.employee),
        activity: str(v.activity),
      },
      extra: {
        projectName: str(v.projectName),
        taxable: v.taxable !== false,
        employeeRate: typeof v.employeeRate === 'number' ? v.employeeRate : null,
      },
    };
  });
}

// The client choice for a project whose client isn't in the studio yet.
export const NEW_CLIENT = 'new';

// Archived vendors and tax rates are left out unless ticked. Archived clients
// and projects come in (projects as inactive): they hold the studio's history,
// and time entries and rooms need their projects.
const ARCHIVED_UNSELECTED = new Set(['vendors', 'taxLocations']);

// The review table's rows for one sheet. Rows without what identifies them (a
// name, a project) still come through so the review can flag them, except rows
// with no name at all, which are counted and left out the way the product
// import does.
export function buildRecordRows(type, { sheet, mapping, context = {} }) {
  let entries;
  let combineKey = null;
  let merge;
  // Rows a mixed contact list labels for the other import: vendors in a client
  // import, and anyone not labelled a vendor in a vendor import.
  let otherRole = () => false;
  switch (type) {
    case 'customers':
      entries = customerEntries(sheet, mapping);
      combineKey = (e) =>
        e.extra.accountName
          ? `account|${norm(e.extra.accountName)}`
          : e.fields.name && `${norm(e.fields.name)}|${norm(e.fields.email)}`;
      merge = mergeHousehold;
      otherRole = (e) => e.vendorRow;
      break;
    case 'vendors':
      entries = vendorEntries(sheet, mapping);
      combineKey = (e) => norm(e.fields.name);
      if (mapping.includes('role')) otherRole = (e) => !e.vendorRow;
      break;
    case 'taxLocations':
      entries = taxEntries(sheet, mapping);
      combineKey = (e) => `${norm(e.fields.city)}|${norm(e.fields.state)}|${e.fields.rate}`;
      break;
    case 'projects':
      entries = projectEntries(sheet, mapping);
      // Ivy ties time entries and rooms to a project by name alone, so two Ivy
      // projects with one name and client can't be told apart; they become one.
      combineKey = (e) =>
        e.fields.name &&
        `${norm(e.fields.name)}|${norm(e.extra.clientName) || norm(e.extra.email)}`;
      merge = (existing, entry) => {
        const here = addressLine({ ...existing.fields, ...existing.extra });
        const there = addressLine({ ...entry.fields, ...entry.extra });
        if (there && norm(there) !== norm(here) && !existing.extra.alsoAt.includes(there)) {
          existing.extra.alsoAt.push(there);
        }
        if (
          entry.extra.createdAt &&
          (!existing.extra.createdAt || entry.extra.createdAt < existing.extra.createdAt)
        ) {
          existing.extra.createdAt = entry.extra.createdAt;
        }
        if (entry.fields.status === 'active') existing.fields.status = 'active';
      };
      break;
    case 'rooms':
      entries = roomEntries(sheet, mapping);
      combineKey = (e) => e.fields.name && `${norm(e.extra.projectName)}|${norm(e.fields.name)}`;
      break;
    case 'timeEntries':
      entries = timeEntries(sheet, mapping);
      break;
    default:
      throw new Error(`Unknown record type ${type}`);
  }

  const ownRole = entries.filter((e) => !otherRole(e));
  const otherRoleRows = entries.length - ownRole.length;
  const named =
    type === 'timeEntries'
      ? ownRole
      : ownRole.filter(
          (e) => e.fields.name || (type === 'taxLocations' && (e.fields.city || e.fields.state))
        );
  const missingName = ownRole.length - named.length;
  const placeholderEmails = named.reduce((sum, e) => sum + (e.placeholderEmails || 0), 0);
  const combined = combineKey ? combineRows(named, combineKey, merge) : named;

  const findProject = projectMatcher(context.projects || []);
  const findClient = clientMatcher(context.customers || []);
  const findTax = taxMatcher(context.taxLocations || []);
  const nameCounts = new Map();
  if (type === 'customers') {
    combined.forEach((e) =>
      nameCounts.set(norm(e.fields.name), (nameCounts.get(norm(e.fields.name)) || 0) + 1)
    );
  }

  let existingCount = 0;
  let archivedCount = 0;
  const existingIndex = buildExistingIndex(type, context);
  const rows = combined.map((entry, index) => {
    const row = {
      id: `r-${entry.sourceRows[0]}-${index}`,
      importRef: entry.importRef,
      sourceRows: entry.sourceRows,
      archived: entry.archived,
      fields: entry.fields,
      extra: entry.extra,
      flags: {},
    };
    if (type === 'customers' && nameCounts.get(norm(row.fields.name)) > 1)
      row.flags.duplicateName = true;
    if (type === 'projects') {
      const match = findClient({ name: row.extra.clientName, email: row.extra.email });
      if (match) {
        row.fields.clientChoice = match.customer.customerId;
        row.flags.ambiguousClient = match.ambiguous;
      } else if (row.extra.clientName) {
        row.fields.clientChoice = NEW_CLIENT;
      }
      const tax = row.extra.taxName ? findTax(row.extra.taxName) : null;
      if (tax) row.fields.taxChoice = tax.id;
    }
    if (type === 'rooms' || type === 'timeEntries') {
      const match = findProject(row.extra.projectName);
      if (match) {
        row.fields.projectId = match.project.projectId;
        row.flags.ambiguousProject = match.ambiguous;
      }
    }

    row.existing = existingReason(type, row, context, { index: existingIndex });
    if (row.existing) existingCount += 1;
    else if (row.archived && ARCHIVED_UNSELECTED.has(type)) archivedCount += 1;
    row.selected = !row.existing && !(row.archived && ARCHIVED_UNSELECTED.has(type));
    return row;
  });

  return {
    rows,
    stats: {
      combinedRows: named.length - combined.length,
      // Of those, people added to their household's client.
      householdMerges: rows.reduce(
        (sum, r) => sum + (r.extra.accountName ? r.sourceRows.length - 1 : 0),
        0
      ),
      otherRoleRows,
      missingName,
      existing: existingCount,
      archivedUnselected: archivedCount,
      archivedIncluded: ARCHIVED_UNSELECTED.has(type)
        ? 0
        : rows.filter((r) => r.archived && r.selected).length,
      placeholderEmails,
    },
  };
}

/* ============================================================
   Checks before import
============================================================ */

// [one, many], blocking or not, per issue. Blocking ones stop the import.
export const RECORD_ISSUES = {
  name: { blocking: true, phrase: ['needs a name', 'need a name'], title: 'Name required' },
  place: {
    blocking: true,
    phrase: ['needs a city or state', 'need a city or state'],
    title: 'City or state required',
  },
  rate: { blocking: true, phrase: ['needs a rate', 'need a rate'], title: 'Rate required' },
  project: {
    blocking: true,
    phrase: ["isn't matched to a project", "aren't matched to a project"],
    title: 'No project',
  },
  date: { blocking: true, phrase: ['needs a date', 'need a date'], title: 'Date required' },
  hours: { blocking: true, phrase: ['needs hours', 'need hours'], title: 'Hours required' },
  newClient: {
    blocking: false,
    phrase: ['will add a new client', 'will add new clients'],
    title: 'New clients',
  },
  noClient: { blocking: false, phrase: ['has no client', 'have no client'], title: 'No client' },
  ambiguousClient: {
    blocking: false,
    phrase: [
      'matched one of several clients with the same name',
      'matched one of several clients with the same name',
    ],
    title: 'Several clients match',
  },
  tax: {
    blocking: false,
    phrase: [
      "names a sales tax this studio doesn't have",
      "name a sales tax this studio doesn't have",
    ],
    title: 'Sales tax not found',
  },
  ambiguousProject: {
    blocking: false,
    phrase: [
      'matched one of several projects with the same name',
      'matched one of several projects with the same name',
    ],
    title: 'Several projects match',
  },
  duplicateName: {
    blocking: false,
    phrase: [
      'shares its name with another client in this file',
      'share a name with other clients in this file',
    ],
    title: 'Same name as another client',
  },
  email: { blocking: false, phrase: ['has no email', 'have no email'], title: 'No email' },
  zeroHours: { blocking: false, phrase: ['has 0 hours', 'have 0 hours'], title: '0 hours' },
  tooBig: {
    blocking: true,
    phrase: ['is too big to import in one piece', 'are too big to import in one piece'],
    title: 'Too big to import',
  },
  totalDiffers: {
    blocking: false,
    phrase: [
      'comes to a different total than in the old system',
      'come to different totals than in the old system',
    ],
    title: 'Total differs',
  },
  unpriced: {
    blocking: false,
    phrase: ['has lines without a price', 'have lines without a price'],
    title: 'Lines without a price',
  },
  largeProposal: {
    blocking: false,
    phrase: [
      'has over 60 lines, more than the proposal editor may be able to save',
      'have over 60 lines, more than the proposal editor may be able to save',
    ],
    title: 'Over 60 lines',
  },
};

export const RECORD_ISSUE_KEYS = {
  customers: ['name', 'duplicateName', 'email'],
  vendors: ['name'],
  taxLocations: ['place', 'rate'],
  projects: ['name', 'newClient', 'noClient', 'ambiguousClient', 'tax'],
  rooms: ['project', 'name', 'ambiguousProject'],
  proposals: ['project', 'tooBig', 'ambiguousProject', 'totalDiffers', 'unpriced', 'largeProposal'],
  timeEntries: ['project', 'date', 'hours', 'zeroHours', 'ambiguousProject'],
};

// The server takes up to 500 lines a proposal, in a request under its 100 kB
// body limit. The proposal editor saves the whole proposal in one request with
// every line's full detail, which runs out at roughly 65 lines.
export const MAX_PROPOSAL_LINES = 500;
export const MAX_PROPOSAL_BYTES = 90000;
export const LARGE_PROPOSAL_LINES = 60;

export function recordRowIssues(type, row) {
  const f = row.fields;
  const issues = [];
  const add = (key, when) => when && issues.push(key);
  switch (type) {
    case 'customers':
      add('name', !f.name.trim());
      add('duplicateName', row.flags.duplicateName);
      add('email', !f.email.trim());
      break;
    case 'vendors':
      add('name', !f.name.trim());
      break;
    case 'taxLocations': {
      const rate = readNumber(f.rate);
      add('place', !f.city.trim() && !f.state.trim());
      add('rate', rate === null || rate < 0 || rate >= 100);
      break;
    }
    case 'projects':
      add('name', !f.name.trim());
      add('newClient', f.clientChoice === NEW_CLIENT);
      add('noClient', !f.clientChoice);
      add(
        'ambiguousClient',
        row.flags.ambiguousClient && f.clientChoice !== NEW_CLIENT && Boolean(f.clientChoice)
      );
      add('tax', Boolean(row.extra.taxName) && !f.taxChoice);
      break;
    case 'rooms':
      add('project', !f.projectId);
      add('name', !f.name.trim());
      add('ambiguousProject', row.flags.ambiguousProject && Boolean(f.projectId));
      break;
    case 'timeEntries': {
      const hours = readNumber(f.hours);
      add('project', !f.projectId);
      add('date', !readDate(f.date));
      add('hours', hours === null || hours < 0);
      add('zeroHours', hours === 0);
      add('ambiguousProject', row.flags.ambiguousProject && Boolean(f.projectId));
      break;
    }
    case 'proposals': {
      const x = row.extra;
      add('project', !f.projectId);
      add('tooBig', x.lines.length > MAX_PROPOSAL_LINES || x.payloadBytes > MAX_PROPOSAL_BYTES);
      add('ambiguousProject', row.flags.ambiguousProject && Boolean(f.projectId));
      add('totalDiffers', x.sourceTotal !== null && Math.abs(x.total - x.sourceTotal) > 1);
      add('unpriced', x.unpricedLines > 0);
      add('largeProposal', x.lines.length > LARGE_PROPOSAL_LINES);
      break;
    }
    default:
  }
  return issues;
}

// Counts each issue across the selected rows.
export function summarizeRecordRows(type, rows) {
  const keys = RECORD_ISSUE_KEYS[type];
  const issues = Object.fromEntries(keys.map((key) => [key, 0]));
  let selected = 0;
  rows.forEach((row) => {
    if (!row.selected) return;
    selected += 1;
    recordRowIssues(type, row).forEach((key) => {
      issues[key] += 1;
    });
  });
  const blocking = keys
    .filter((key) => RECORD_ISSUES[key].blocking)
    .reduce((sum, key) => sum + issues[key], 0);
  return { selected, issues, blocking };
}

/* ============================================================
   Proposal totals and tax
============================================================ */

// Rounded the way the proposal code rounds.
export const round2 = (value) => Number((Number(value) || 0).toFixed(2));

// Settings keeps rates as "9.5%" text; an older entry may hold the fraction.
function locationRate(location) {
  const value = location?.tax ?? location?.rate;
  if (typeof value === 'number') return value > 1 ? value / 100 : value;
  const rate = readNumber(value);
  return rate === null ? 0 : rate / 100;
}

// The sales tax a proposal is saved with, as a fraction: the rate the old
// system charged when the file says (Ivy does), else the rate of the project's
// tax location. The location is linked only when its rate is the one used.
export function proposalTax(row, context = {}) {
  const project = (context.projects || []).find((p) => p.projectId === row.fields.projectId);
  const location = project?.salesTaxRate
    ? (context.taxLocations || []).find((t) => t.id === project.salesTaxRate) || null
    : null;
  const projectRate = location ? locationRate(location) : 0;
  const rate = row.extra.salesTaxRate ?? projectRate;
  const linked = location && Math.abs(projectRate - rate) < 0.000001;
  return { rate, locationId: linked ? location.id : '' };
}

// Subtotal, tax and total the way a saved proposal works them out: each line
// is its unit price times quantity plus its shipping; tax covers taxable lines,
// and taxable shipping on lines that aren't credits.
export function proposalTotals(lines, rate) {
  let subtotal = 0;
  let taxableMerch = 0;
  let taxableShipping = 0;
  lines.forEach((l) => {
    const merch = round2(l.clientUnit * l.qty);
    subtotal += round2(merch + l.clientShipping);
    if (l.taxable) taxableMerch += merch;
    if (l.shippingTaxable && l.qty > 0) taxableShipping += l.clientShipping;
  });
  subtotal = round2(subtotal);
  const tax = round2((round2(taxableMerch) + round2(taxableShipping)) * rate);
  return { subtotal, tax, total: round2(subtotal + tax) };
}

// A proposal line without its empty fields, which the server fills in anyway;
// it keeps big proposals under the request size limit.
export function compactLine(l) {
  return Object.fromEntries(
    Object.entries(l).filter(
      ([, value]) =>
        value !== '' &&
        value !== 0 &&
        value !== false &&
        value != null &&
        !(Array.isArray(value) && !value.length)
    )
  );
}

/* ============================================================
   Saving
============================================================ */

// The client a project row creates when its client isn't in the studio yet,
// built from the project's own contact details. Rows naming the same client
// share one.
export function newClientKey(row) {
  return `${norm(row.extra.clientName)}|${norm(row.extra.email)}`;
}

export function newClientPayload(row) {
  return {
    importRef: `${row.importRef}:client`,
    name: row.extra.clientName,
    email: row.extra.email,
    secondaryEmail: row.extra.secondaryEmail,
    phone: row.extra.phone,
    billingStreet: row.fields.street,
    billingStreet2: row.extra.street2,
    billingCity: row.fields.city,
    billingState: row.extra.state,
    billingZip: row.extra.zip,
  };
}

// Whether a time entry was already billed in the old system: all of them, those
// before a date, or none.
export function isBilledBefore(row, billed) {
  if (billed.mode === 'all') return true;
  if (billed.mode === 'before') return Boolean(billed.before) && row.fields.date < billed.before;
  return false;
}

// What the record import endpoint is sent for one row. `customer` is the
// project's client: one already in the studio or one this import just created.
// `context` is what the studio has, for a proposal's project and tax rate.
export function recordPayload(
  type,
  row,
  { customer = null, billed, billedLabel = '', context = {} } = {}
) {
  const f = row.fields;
  const x = row.extra;
  switch (type) {
    case 'customers': {
      const secondary = splitName(x.secondaryName);
      // Who's who stays in the notes: the household's people, or one person's role.
      const people = x.accountName
        ? `People: ${x.people.map((p) => (p.role ? `${p.name} (${p.role})` : p.name)).join('; ')}`
        : x.people[0]?.role && `Role: ${x.people[0].role}`;
      return {
        importRef: row.importRef,
        name: f.name.trim(),
        company: x.company,
        email: f.email.trim(),
        secondaryEmail: x.secondaryEmail,
        phone: f.phone.trim(),
        secondaryPhone: x.secondaryPhone,
        secondaryFirstName: secondary.first,
        secondaryLastName: secondary.last,
        billingStreet: f.street.trim(),
        billingStreet2: x.street2,
        billingCity: f.city.trim(),
        billingState: readState(f.state),
        billingZip: f.zip.trim(),
        notes: [x.notes, people, x.tags && `Tags: ${x.tags}`].filter(Boolean).join('\n'),
      };
    }
    case 'vendors':
      return {
        importRef: row.importRef,
        name: f.name.trim(),
        firstName: x.firstName,
        lastName: x.lastName,
        email: f.email.trim(),
        phone: f.phone.trim(),
        accountNumber: x.accountNumber,
        link: f.link.trim(),
        streetAddress: x.street,
        city: f.city.trim(),
        state: readState(f.state),
        zip: x.zip,
        internalNotes: [x.description, x.notes].filter(Boolean).join('\n'),
      };
    case 'taxLocations':
      return {
        importRef: row.importRef,
        city: f.city.trim(),
        state: readState(f.state),
        rate: readNumber(f.rate),
        agency: f.agency.trim(),
        updated: localToday(),
      };
    case 'projects': {
      const c = customer || {};
      const notes = [x.notes, x.alsoAt.length ? `Also listed at: ${x.alsoAt.join('; ')}` : '']
        .filter(Boolean)
        .join('\n\n');
      return {
        importRef: row.importRef,
        projectNickname: f.name.trim(),
        customerId: c.customerId || '',
        customerName: c.name || x.clientName,
        email: x.email || c.email || '',
        secondaryEmail: x.secondaryEmail || c.secondaryEmail || '',
        phone: x.phone || c.phone || '',
        secondaryPhone: c.secondaryPhone || '',
        billingStreet: c.billingStreet || '',
        billingStreet2: c.billingStreet2 || '',
        billingCity: c.billingCity || '',
        billingState: c.billingState || '',
        billingZip: c.billingZip || '',
        projectStreet: f.street.trim(),
        projectStreet2: x.street2,
        projectCity: f.city.trim(),
        projectState: x.state,
        projectZip: x.zip,
        notes,
        salesTaxRate: f.taxChoice,
        isActive: f.status !== 'archived',
        createdAt: x.createdAt,
      };
    }
    case 'rooms':
      return { importRef: row.importRef, projectId: f.projectId, name: f.name.trim() };
    case 'proposals': {
      const project = (context.projects || []).find((p) => p.projectId === f.projectId);
      const tax = proposalTax(row, context);
      return {
        importRef: row.importRef,
        projectId: f.projectId,
        proposalNumber: f.proposalNumber.trim(),
        title: f.title.trim(),
        date: f.date,
        createdAt: x.createdAt,
        status: f.status,
        salesTaxRate: tax.rate,
        taxLocationId: tax.locationId,
        notes: x.notes,
        client: {
          name: x.clientName || project?.customerName || '',
          email: x.clientEmail || project?.email || '',
        },
        projectName: project?.name || x.projectName,
        billingAddress: x.billingAddress,
        lineItems: x.lines.map(compactLine),
      };
    }
    case 'timeEntries':
      return {
        importRef: row.importRef,
        projectId: f.projectId,
        date: f.date,
        billableHours: readNumber(f.hours),
        rate: readNumber(f.rate) ?? 0,
        ...(x.employeeRate !== null && { employeeRate: x.employeeRate }),
        description: f.description.trim(),
        employee: f.employee.trim(),
        activity: f.activity.trim(),
        billable: Boolean(f.billable),
        taxable: x.taxable,
        billedLabel: billed && isBilledBefore(row, billed) ? billedLabel : '',
      };
    default:
      throw new Error(`Unknown record type ${type}`);
  }
}

// Splits records into requests the server accepts: at most `maxCount` each,
// and small enough to stay under its 100 kB body limit.
export function chunkRecords(records, { maxCount = 100, maxBytes = 60000 } = {}) {
  // The limit is in bytes, and notes can hold characters that take three.
  const encoder = typeof TextEncoder === 'undefined' ? null : new TextEncoder();
  const sizeOf = (record) => {
    const json = JSON.stringify(record);
    return encoder ? encoder.encode(json).length : json.length * 3;
  };
  const chunks = [];
  let current = [];
  let bytes = 0;
  records.forEach((record) => {
    const size = sizeOf(record);
    if (current.length && (current.length >= maxCount || bytes + size > maxBytes)) {
      chunks.push(current);
      current = [];
      bytes = 0;
    }
    current.push(record);
    bytes += size;
  });
  if (current.length) chunks.push(current);
  return chunks;
}

// A short label for a row in the results and the report.
export function recordLabel(type, row) {
  const f = row.fields;
  switch (type) {
    case 'taxLocations':
      return [f.city, f.state].filter(Boolean).join(', ');
    case 'rooms':
      return `${f.name} (${row.extra.projectName})`;
    case 'proposals':
      return `${f.proposalNumber || f.title || 'New proposal'} (${row.extra.projectName})`;
    case 'timeEntries':
      return `${f.date} · ${row.extra.projectName} · ${f.hours} h`;
    default:
      return f.name;
  }
}
